// ============================================================================
// ChatInfoPanel — правая панель «контекстная карточка» чата (волна 2, D6).
//   desktop: КОЛОНКА справа 380px внутри layout чата (лента сжимается,
//   панель не перекрывает переписку); mobile: полноэкранная (адаптив,
//   как ContextChatPanel). Секции: «Сущность» (контекстный чат), «Участники»,
//   «Закреплённые», «Файлы/медиа/ссылки». Стекло — .glass-panel, вкладки —
//   единый TabBar, подписи — русские.
// УПРАВЛЕНИЕ ГРУППОЙ (§2.3): переименование (создатель/админ — кнопка у
// названия), добавление/удаление участников («Участники»), выход из группы
// (все участники; если создатель — предупреждение о передаче прав). Права
// проверяет СЕРВЕР; панель обновляется событием chat.conversation.updated.{id}.
// Данные: RPC ядра через chatBridge; chatInfoData — сущность/файлы.
// ============================================================================

import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { Conversation } from '@zdc/sdk';
import { LogOut, Pencil, X } from 'lucide-react';
import { TabBar } from '@/components/ui/TabBar';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { friendlyError, logSwallowed } from '@/lib/errors';
import { confirmDialog, promptDialog } from '@/lib/dialogs';
import { useToastStore } from '@/store/toastStore';
import { createLogger } from '@/lib/logger';
import { eventBusHub } from '@/lib/eventBusHub';
import {
  getConversation, createConversation,
  removeConversationMember, leaveConversation, setConversationTitle,
} from '@/lib/chatBridge';
import { openConversation } from '@/lib/chatNavigation';
import { ChatEntityBlock } from './ChatEntityBlock';
import { ChatInfoAvatar } from './ChatInfoAvatar';
import { ChatMembersBlock } from './ChatMembersBlock';
import { ChatPinnedBlock } from './ChatPinnedBlock';
import { ChatSharedBlock } from './ChatSharedBlock';
import { AddChatMemberDialog } from './AddChatMemberDialog';
import {
  collectChatAttachments,
  collectChatLinks,
  formatStamp,
  type ChatInfoMessage,
} from './chatInfoData';
import type { ThreadUser } from './ChatThread';

const log = createLogger('ChatInfoPanel');

interface ChatInfoPanelProps {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  title: string;
  myId: string;
  users: ThreadUser[];
  memberIds?: string[];
  memberLastSeen?: Record<string, string>;
  contextType?: string;
  contextCode?: string;
  contextId?: string;
  messages?: ChatInfoMessage[];
  onPreview: (fileId: string, fileName: string) => void;
  /** Открыть диалог (ChatPage: точечно выбрать; иначе — единая навигация). */
  onOpenConversation?: (convId: string) => void;
  /** D6: переход к закреплённому сообщению в ленте (прокрутка Virtuoso). */
  onJumpToMessage: (messageId: string) => void;
  /** Администратор базы (создатель чата или админ — управление группой). */
  isAdmin?: boolean;
  /** Узкий контейнер (контекстная панель/виджет): на desktop — поверх, не колонка. */
  overlay?: boolean;
  /** После «Покинуть группу»: закрыть чат/перейти к списку (родитель). */
  onLeftConversation?: () => void;
}

type InfoTab = 'about' | 'members' | 'pinned' | 'shared';

const KIND_LABELS: Record<string, string> = {
  direct: 'Личный чат',
  group: 'Групповой чат',
};

export function ChatInfoPanel({
  open, onClose, conversationId, title, myId, users,
  memberIds, memberLastSeen, contextType, contextCode, contextId,
  messages, onPreview, onOpenConversation, onJumpToMessage,
  isAdmin = false, overlay = false, onLeftConversation,
}: ChatInfoPanelProps) {
  const { isMobile } = useBreakpoint();
  // Колонка возможна только в широком контейнере; в узком (панель обсуждения,
  // чат-виджет) — прежний выезд поверх, чтобы не сжать ленту в ноль.
  const desktopOverlay = overlay && !isMobile;
  const [tab, setTab] = useState<InfoTab>('about');
  const [conv, setConv] = useState<Conversation | null>(null);
  // Состав/название после действия или события шины (серверные значения SSoT)
  // перекрывают устаревший проп memberIds, пока родитель не перечитал список.
  const [membersOverride, setMembersOverride] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // Свой выход из группы: событие сервера не должно повторно показывать
  // «Вы больше не участник» (мы уже закрыли панель по своей воле).
  const leftRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setMembersOverride(null);
    setAddOpen(false);
    getConversation(conversationId)
      .then((c) => { if (alive) setConv(c); })
      .catch((e) => logSwallowed(e, 'components/chat/ChatInfoPanel.tsx:conversation'));
    return () => { alive = false; };
  }, [open, conversationId]);

  // LIVE (§2.3): структурные изменения группы (участник добавлен/удалён/вышел,
  // переименование) приходят событием chat.conversation.updated.{id} с
  // актуальным составом — название/создатель/участники обновляются сразу.
  // Если я больше не в составе — меня удалили: закрываем чат и уходим к списку.
  useEffect(() => {
    if (!open) return;
    const topic = `chat.conversation.updated.${conversationId}`;
    return eventBusHub.subscribe((ev) => {
      if (ev.topic !== topic) return;
      const p = ev.payload || {};
      const next = Array.isArray(p.member_ids) ? (p.member_ids as unknown[]).map(String) : [];
      setConv((prev) => prev ? {
        ...prev,
        title: p.title !== undefined ? String(p.title) : prev.title,
        createdBy: p.created_by !== undefined ? String(p.created_by) : prev.createdBy,
        memberIds: next.length > 0 ? next : prev.memberIds,
      } : prev);
      if (next.length > 0) setMembersOverride(next);
      if (next.length > 0 && myId && !next.includes(myId) && !leftRef.current) {
        useToastStore.getState().showWarning('Вы больше не участник этого чата');
        onClose();
        onLeftConversation?.();
      }
    });
  }, [open, conversationId, myId, onClose, onLeftConversation]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const list = messages || [];
  const attachments = useMemo(() => collectChatAttachments(list), [list]);
  const links = useMemo(() => collectChatLinks(list), [list]);

  const ctxType = contextType || conv?.contextType || '';
  const ctxCode = contextCode || conv?.contextCode || '';
  const ctxId = contextId || conv?.contextId || '';
  const isContext = Boolean(ctxType && ctxCode && ctxId);

  const ids = membersOverride
    ?? (memberIds && memberIds.length > 0 ? memberIds : (conv?.memberIds || []));
  const seen = memberLastSeen && Object.keys(memberLastSeen).length > 0
    ? memberLastSeen
    : (conv?.memberLastSeen || {});

  const kindLabel = isContext ? 'Обсуждение объекта' : (KIND_LABELS[conv?.kind || ''] || 'Чат');

  // Управление группой (§2.3): права — создатель чата (created_by) или
  // администратор; у direct-чата действий группы нет (правило сервера).
  const isGroup = (conv?.kind || '') === 'group';
  const createdBy = String(conv?.createdBy || '');
  const canManage = isGroup && (isAdmin || (!!createdBy && createdBy === myId));
  // Название: серверное (после переименования/события), иначе — проп родителя
  const displayTitle = (conv?.title && conv.title.trim()) || title;
  // Аватар шапки: личный чат — собеседник; группа/обсуждение — название.
  const groupStyle = isGroup || isContext;
  const otherId = !groupStyle ? ids.find((id) => id !== myId) : undefined;

  const handleWrite = async (memberId: string): Promise<void> => {
    try {
      const { id } = await createConversation({ kind: 'direct', memberIds: [memberId] });
      onClose();
      if (onOpenConversation) onOpenConversation(id);
      else openConversation(id);
    } catch (e) {
      log.error('не удалось открыть личный чат', e);
      useToastStore.getState().showError(friendlyError(e));
    }
  };

  // Переименование группы (создатель/админ; «Создатель» — права сервера)
  const handleRename = async () => {
    const current = conv?.title || title;
    const next = await promptDialog('Название группы', current, 'Переименовать группу', 'Название группы');
    if (next === null) return;
    const t = next.trim();
    if (!t || t === current) return;
    setBusy(true);
    try {
      const c = await setConversationTitle(conversationId, t);
      if (c) setConv(c);
      useToastStore.getState().showSuccess('Группа переименована');
    } catch (e) {
      log.error('не удалось переименовать группу', e);
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  // Удаление участника (подтверждение и ошибку показывает блок участников)
  const handleRemoveMember = async (memberId: string): Promise<void> => {
    const c = await removeConversationMember(conversationId, memberId);
    if (c) {
      setConv(c);
      setMembersOverride(c.memberIds || []);
    } else {
      setMembersOverride((prev) => (prev ?? ids).filter((x) => x !== memberId));
    }
    useToastStore.getState().showSuccess('Участник удалён из группы');
  };

  // Участник добавлен (диалог уже вызвал RPC ядра) — состав с сервера
  const handleAdded = (c: Conversation | null) => {
    if (!c) return;
    setConv(c);
    setMembersOverride(c.memberIds || []);
  };

  // Выход из группы: создатель — предупреждение о передаче прав (сервер
  // передаёт права первому оставшемуся); последний участник выйти не может.
  const handleLeave = async () => {
    const amCreator = !!createdBy && createdBy === myId;
    const ok = await confirmDialog(
      amCreator
        ? 'Покинуть группу? Вы создатель — права создателя перейдут первому оставшемуся участнику.'
        : 'Покинуть группу?',
      'Выход из группы',
    );
    if (!ok) return;
    leftRef.current = true;
    setBusy(true);
    try {
      await leaveConversation(conversationId);
      useToastStore.getState().showInfo('Вы покинули группу');
      onClose();
      onLeftConversation?.();
    } catch (e) {
      leftRef.current = false;
      log.error('не удалось покинуть группу', e);
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const tabs = [
    { id: 'about', label: isContext ? 'Сущность' : 'О чате' },
    { id: 'members', label: `Участники${ids.length ? ` (${ids.length})` : ''}` },
    { id: 'pinned', label: 'Закреплённые' },
    { id: 'shared', label: `Файлы${attachments.length ? ` (${attachments.length})` : ''}` },
  ];

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          key="chat-info-panel"
          role={isMobile || desktopOverlay ? 'dialog' : 'complementary'}
          aria-label="О чате"
          className={
            isMobile
              ? 'fixed inset-0 z-[12500] flex flex-col bg-sidebar pb-[env(safe-area-inset-bottom)]'
              : desktopOverlay
                ? 'fixed inset-y-0 right-0 z-[12500] flex w-[380px] max-w-[94vw] flex-col bg-sidebar border-l shadow-2xl'
                : 'flex h-full shrink-0 flex-col overflow-hidden border-l bg-sidebar shadow-xl'
          }
          initial={(isMobile || desktopOverlay) ? { x: '100%' } : { width: 0, opacity: 0 }}
          animate={(isMobile || desktopOverlay) ? { x: 0 } : { width: 380, opacity: 1 }}
          exit={(isMobile || desktopOverlay) ? { x: '100%' } : { width: 0, opacity: 0 }}
          transition={{ type: 'tween', duration: 0.18 }}
        >
          {/* Desktop — КОЛОНКА справа: лента сжимается, панель не перекрывает
              чат; фиксированная ширина внутри анимируемой оболочки даёт
              «выезд» без перестройки содержимого. */}
          <div className={isMobile ? 'flex h-full min-h-0 flex-col' : 'flex h-full min-h-0 w-[380px] flex-col'}>
            <div className="flex items-center gap-2 border-b px-3 py-2 shrink-0 glass-panel rounded-none border-x-0 border-t-0">
              <ChatInfoAvatar
                title={displayTitle}
                isGroup={groupStyle}
                otherId={otherId}
                users={users}
                size={32}
              />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold leading-none">О чате</div>
                <div className="mt-0.5 truncate text-[10px] text-muted-foreground" title={displayTitle}>{displayTitle}</div>
              </div>
              {canManage && (
                <button
                  type="button"
                  className="shrink-0 rounded-full p-1.5 hover:bg-muted/50 text-muted-foreground"
                  title="Переименовать группу"
                  aria-label="Переименовать группу"
                  disabled={busy}
                  onClick={() => void handleRename()}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                className="shrink-0 rounded-full p-1.5 hover:bg-muted/50"
                onClick={onClose}
                aria-label="Закрыть панель"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-2 pt-2 shrink-0">
              <TabBar group="chat-info-tabs" active={tab} onChange={(id) => setTab(id as InfoTab)} tabs={tabs} />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-2.5 space-y-2.5">
              {tab === 'about' && (
                isContext ? (
                  <ChatEntityBlock contextType={ctxType} contextCode={ctxCode} contextId={ctxId} />
                ) : (
                  <div className="glass-panel rounded-xl p-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <ChatInfoAvatar title={displayTitle} isGroup={groupStyle} otherId={otherId} users={users} size={40} />
                      <div className="min-w-0 text-[14px] font-semibold break-words">{displayTitle}</div>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="rounded-full bg-muted px-2 py-0.5">{kindLabel}</span>
                      <span>{ids.length ? `участников: ${ids.length}` : 'участники не загружены'}</span>
                    </div>
                    {conv?.createdAt && (
                      <div className="text-[10px] text-muted-foreground">Создан {formatStamp(conv.createdAt)}</div>
                    )}
                  </div>
                )
              )}

              {tab === 'members' && (
                <>
                  <ChatMembersBlock
                    memberIds={ids}
                    users={users}
                    myId={myId}
                    memberLastSeen={seen}
                    onWrite={handleWrite}
                    createdBy={createdBy}
                    canManage={canManage}
                    onAddMember={isGroup ? () => setAddOpen(true) : undefined}
                    onRemoveMember={canManage ? handleRemoveMember : undefined}
                  />
                  {/* Выход из группы — любой участник (правила сервера:
                      создатель передаёт права, последний выйти не может) */}
                  {isGroup && (
                    <button
                      type="button"
                      className="w-full rounded-xl border border-destructive/30 px-3 py-2 text-[11px] text-destructive hover:bg-destructive/10 disabled:opacity-40"
                      disabled={busy}
                      onClick={() => void handleLeave()}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <LogOut className="h-3.5 w-3.5" />
                        Покинуть группу
                      </span>
                    </button>
                  )}
                </>
              )}

              {tab === 'pinned' && (
                <ChatPinnedBlock
                  conversationId={conversationId}
                  myId={myId}
                  users={users}
                  onJump={(id) => { onJumpToMessage(id); onClose(); }}
                />
              )}

              {tab === 'shared' && (
                <ChatSharedBlock attachments={attachments} links={links} onPreview={onPreview} />
              )}
            </div>

            {/* Добавление участника (§2.3): люди (peopleOnly), без уже состоящих */}
            <AddChatMemberDialog
              open={addOpen}
              onClose={() => setAddOpen(false)}
              conversationId={conversationId}
              excludeIds={[myId, ...ids]}
              onAdded={handleAdded}
            />
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
